import { useEffect, useState } from 'react';
import type { LinkedTicketSummary } from '@techgitter/contracts';
import { fetchLinkedTickets } from '../services/workItemDataService';

/**
 * Jobs raised from `jobGuid` via the ticket form's Link button. Pass `null`
 * to skip the fetch (unsaved ticket, collapsed card). A failure reads as an
 * empty list and is logged — the chips are context, not something a page
 * should break over.
 */
export function useLinkedTickets(jobGuid: string | null | undefined): {
  children: LinkedTicketSummary[];
  isLoading: boolean;
} {
  const [children, setChildren] = useState<LinkedTicketSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!jobGuid) {
      setChildren([]);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    fetchLinkedTickets(jobGuid, controller.signal)
      .then(setChildren)
      .catch(err => {
        if (controller.signal.aborted) return;
        console.error('[useLinkedTickets] Failed to load linked tickets:', err);
        setChildren([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [jobGuid]);

  return { children, isLoading };
}
